/**
 * Bloque 5 del rediseño de alta/activación.
 *
 * Vista centralizada "Cuentas" por edificio — reemplaza la mezcla de
 * GestionUsuarios.jsx + OnboardingResidente.jsx con una sola pantalla:
 *  - 5 KPI cards (Activas / Invitadas / Vencidas / Sin enviar / Archivadas)
 *  - Buscador + filtros
 *  - DataTable con StatusBadge derivado de computeAccountState
 *  - Modal único de invitar (detecta residente ya activo, duplicados, etc.)
 *  - Acciones individuales: reenviar, renovar+reenviar, copiar link, archivar,
 *    reactivar, editar datos y dar de baja; solo superadmin, marcar revisada
 *    una cuenta activada con un link en claro (D3)
 *  - Acciones masivas (selección): enviar / reenviar invitación, archivar.
 *
 * Fase 0C de seguridad: los cambios de identidad pasan por los callables de
 * src/utils/identityApi.js. Esta pantalla ya no escribe users, apartments,
 * userbuilding ni occupancies, y no lee activationToken:
 *  - Invitar: inviteResident (modo "preview" para la búsqueda con debounce y
 *    "commit" para el alta, con las confirmaciones que pide el servidor).
 *  - Reenviar / renovar / copiar link / archivar / reactivar (individual y
 *    masivo) y marcar revisada (individual, solo superadmin):
 *    residentAccountAction.
 *  - Editar datos: updateResidentContact. Fecha de ingreso: occupancyAction
 *    "corregir_fechas". Dar de baja: occupancyAction "baja".
 *
 * Estilo: todo del design system (colors, spacing, radius, KPICard, DataTable,
 * StatusBadge, AdminModal, FormField, EmptyState).
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  FaCheckCircle,
  FaPaperPlane,
  FaExclamationTriangle,
  FaInbox,
  FaArchive,
  FaPlus,
  FaSearch,
  FaUserPlus,
  FaRedo,
  FaCopy,
  FaEnvelope,
  FaWhatsapp,
  FaEllipsisV,
  FaPen,
  FaUserSlash,
  FaUserCheck,
} from "react-icons/fa";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db, auth } from "../../firebase";
import {
  colors,
  spacing,
  radius,
  shadows,
  typography,
  pageContainer,
  inputBase,
  btnPrimary,
  btnSecondary,
  btnDanger,
  btnGhost,
} from "../../utils/designTokens";
import KPICard from "../../components/KPICard";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import AdminModal from "../../components/AdminModal";
import FormField from "../../components/FormField";
import ScreenHeader from "../../components/ScreenHeader";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import {
  computeAccountState,
  ACCOUNT_STATES,
  ACCOUNT_STATE_BADGE_KEYS,
  ACCOUNT_STATE_LABELS,
} from "../../utils/accountState";
import {
  DOC_TYPES,
  normalizeDocumento,
  getDocumentoValidationMessage,
  validateCIUY,
  isNumericDocType,
} from "../../utils/documento";
import { ROLE_LABELS } from "../../utils/roleSwitch";
import { normalizePhoneUY, toWaMeDigits } from "../../utils/phoneE164";
import { collectUserIdsForBuilding, fetchUsersByUserIds } from "../../utils/firestoreHelpers";
import { getOccupanciesForBuilding, SENTINEL_START } from "../../utils/occupancies";
import { esAlquilerDelEdificio } from "../../utils/unidadesEdificio";
import {
  inviteResident,
  residentAccountAction,
  updateResidentContact,
  occupancyAction,
  nuevoRequestId,
  mensajeDeError,
  codigoDeError,
} from "../../utils/identityApi";

const KPI_DEFS = [
  {
    state: ACCOUNT_STATES.ACTIVE,
    label: "Activas",
    icon: FaCheckCircle,
    iconBg: colors.successLight,
    iconColor: colors.successDark,
  },
  {
    state: ACCOUNT_STATES.INVITED_ACTIVE,
    label: "Invitadas",
    icon: FaPaperPlane,
    iconBg: colors.infoLight,
    iconColor: colors.infoDark,
  },
  {
    state: ACCOUNT_STATES.INVITED_EXPIRED,
    label: "Vencidas",
    icon: FaExclamationTriangle,
    iconBg: colors.warningLight,
    iconColor: colors.warningDark,
  },
  {
    state: ACCOUNT_STATES.CREATED,
    label: "Sin enviar",
    icon: FaInbox,
    iconBg: colors.gray100,
    iconColor: colors.gray600,
  },
  {
    state: ACCOUNT_STATES.ARCHIVED,
    label: "Archivadas",
    icon: FaArchive,
    iconBg: colors.gray100,
    iconColor: colors.gray400,
  },
];

// Cada unidad tiene titular + adicional para propietario e inquilino.
// `field` es el slot del callable inviteResident.
const ROL_OPTIONS = [
  { label: "Propietario", rol: "Propietario", secondary: false, field: "owner" },
  { label: "Propietario adicional", rol: "Propietario", secondary: true, field: "ownerSecondary" },
  { label: "Inquilino", rol: "Inquilino", secondary: false, field: "tenant" },
  { label: "Inquilino adicional", rol: "Inquilino", secondary: true, field: "tenantSecondary" },
];

// "Alquiler del edificio": el dueño es el edificio, a esa unidad solo se invitan inquilinos.
const rolesParaUnidad = (apt) => (esAlquilerDelEdificio(apt) ? ROL_OPTIONS.filter((o) => o.rol === "Inquilino") : ROL_OPTIONS);

// Hasta que se publiquen las reglas 0D el link de activación solo lo recibe un
// superadmin (contrato residentAccountAction, ronda 3).
const MSJ_LINK_SOLO_SUPERADMIN =
  "Por ahora el link solo lo copia un superadmin. Usá Reenviar: le llega a la persona por WhatsApp o email.";
// Ídem para el email de login de una cuenta ya activada (updateResidentContact, ronda 2).
const MSJ_EMAIL_ACTIVADA =
  "Por ahora, el email de una cuenta ya activada lo cambia un superadmin. Pedíselo a soporte.";
const MSJ_SIN_SALIR = "La invitación quedó emitida pero no salió por WhatsApp ni email.";
const CANAL_TEXTO = { whatsapp: " por WhatsApp", email: " por email", ambos: " por WhatsApp y email" };
// residentAccountAction procesa hasta 50 residentes por llamada.
const LOTE_MAX = 50;
// Omitidos esperables que no hace falta listar en el detalle de una acción masiva.
const OMITIDOS_INOCUOS = new Set(["ya_archivada", "no_archivada"]);
// Estados que entran en el envío masivo de invitaciones ("reenviar": el
// servidor emite un link nuevo para Sin enviar y Vencida).
const ESTADOS_ENVIO_MASIVO = new Set([
  ACCOUNT_STATES.CREATED,
  ACCOUNT_STATES.INVITED_EXPIRED,
  ACCOUNT_STATES.INVITED_ACTIVE,
]);
// inviteResident modo "preview" → estado del banner del modal.
const ESTADO_PREVIEW = { nuevo: "none", existente: "found", conflicto: "conflict", otro_rol: "otherRole" };
// inviteResident modo "commit" → outcome (mismos códigos que el servidor).
const INVITE_OUTCOMES = Object.freeze({
  CREATED: "created",
  REINVITED: "reinvited",
  ASSIGNED_EXISTING: "assigned_existing",
  // K2: la ficha encontrada también está vinculada a otro edificio que el
  // staff no administra. El servidor no asigna la unidad, no emite el link ni
  // avisa: la revisa un superadmin (lo audita y le avisa a operaciones).
  REVISION_SUPERADMIN: "revision_superadmin",
});
// K2 en residentAccountAction: estado de la ficha (reenviar, renovar,
// copiar_link y masivo) cuyo email figura en otra ficha sin cuenta. Sin link ni
// aviso; en el masivo las demás siguen.
const ESTADO_REVISION = "revision_superadmin";
const MSJ_REVISION =
  "Esta persona ya tiene una ficha vinculada a otro edificio; la revisa un superadmin.";
// D3: users.activadaVia "link_en_claro" (lo escribe solo el servidor) = la
// cuenta se activó con un link en claro sin constancia del servidor. Sus
// invitaciones y asignaciones van a revisión hasta que un superadmin la marca
// revisada (residentAccountAction "marcar_revisada": el servidor la pasa a
// "revisada_superadmin:<uid>" y lo audita). El staff no ve la marca.
const ACTIVADA_VIA_LINK_EN_CLARO = "link_en_claro";
const activadaConLinkEnClaro = (u) => u?.activadaVia === ACTIVADA_VIA_LINK_EN_CLARO;
const MSJ_LINK_EN_CLARO =
  "Activó su cuenta con un link de invitación viejo, sin constancia del servidor: sus invitaciones y asignaciones las revisa un superadmin hasta que la marques revisada desde el menú de acciones.";
// Mensaje del servidor de un resultado por ficha (o de la invitación).
const mensajeDe = (r) => String((r && (r.mensaje || r.message)) || "").trim();

// Columna Unidad: más que esto se resume con "+N" (el tooltip las lista todas).
const MAX_UNIDADES_VISIBLES = 4;

const nombreDe = (u) =>
  `${u?.NombreCompleto || u?.name || ""} ${u?.ApellidosCompleto || ""}`.trim() || `ID ${u?.userid ?? ""}`.trim();

const enLotes = (lista, n = LOTE_MAX) => {
  const out = [];
  for (let i = 0; i < lista.length; i += n) out.push(lista.slice(i, i + n));
  return out;
};

const Cuentas = ({ setSelected, userType }) => {
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const { confirmDialog, alertDialog } = useAppDialog();
  const buildingId = selectedBuilding?.buildingid;
  // userType efectivo (App.js): en "vista de soporte" un superadmin ve lo que ve un admin.
  const esSuperadmin = Number(userType) === 2;

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [occupancies, setOccupancies] = useState([]);
  const [search, setSearch] = useState("");
  const [filterState, setFilterState] = useState("all");
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  // R5/T1: baja de residente sin reemplazo (el ocupante se va, no entra nadie).
  const [bajaTarget, setBajaTarget] = useState(null); // { occ, user }
  const [bajaDate, setBajaDate] = useState("");
  const [savingBaja, setSavingBaja] = useState(false);
  const [bajaIntento, terminarBajaIntento] = useIntentoId();
  // Filas (doc id) con una acción en curso: su menú queda deshabilitado.
  const [filasOcupadas, setFilasOcupadas] = useState(() => new Set());
  const filasOcupadasRef = useRef(new Set());

  // -- Carga de datos --------------------------------------------------------
  const loadData = useCallback(async () => {
    if (!selectedBuilding?.buildingid) {
      setUsers([]);
      setApartments([]);
      return;
    }
    setLoading(true);
    try {
      const buildingId = selectedBuilding.buildingid;
      const aptSnap = await getDocs(
        query(
          collection(db, "apartments"),
          where("building", "in", [Number(buildingId), String(buildingId)])
        )
      );
      const aptList = aptSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setApartments(aptList);

      // C10 (reglas 0D): las fichas ajenas se leen con el edificio (accessBids
      // array-contains) + userid; una por userid, la de residente vigente.
      const userIds = await collectUserIdsForBuilding(buildingId);
      const userDocs = await fetchUsersByUserIds(userIds, buildingId);
      const residentes = userDocs.filter((u) => Number(u.typeuser ?? 0) === 0);
      setUsers(residentes);

      // Tenencias del edificio (para marcar ocupantes salientes en la lista).
      try {
        setOccupancies(await getOccupanciesForBuilding(buildingId));
      } catch (occErr) {
        console.error("[Cuentas] no se pudieron cargar occupancies:", occErr);
        setOccupancies([]);
      }
    } catch (err) {
      console.error("[Cuentas] loadData fallo:", err);
      showToast("No se pudieron cargar las cuentas", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedBuilding, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // -- Derivados -------------------------------------------------------------
  const usersWithState = useMemo(
    () =>
      users.map((u) => ({
        ...u,
        accountState: computeAccountState(u),
      })),
    [users]
  );

  const kpis = useMemo(() => {
    const counts = {
      active: 0,
      invited_active: 0,
      invited_expired: 0,
      created: 0,
      archived: 0,
    };
    usersWithState.forEach((u) => {
      counts[u.accountState] = (counts[u.accountState] || 0) + 1;
    });
    return counts;
  }, [usersWithState]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return usersWithState.filter((u) => {
      if (filterState !== "all" && u.accountState !== filterState) return false;
      if (!q) return true;
      const hay =
        `${u.NombreCompleto || ""} ${u.ApellidosCompleto || ""} ${u.email || ""} ${u.celular || ""} ${u.Documento || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [usersWithState, search, filterState]);

  // userid -> TODAS las unidades que ocupa en el edificio (una persona puede
  // ser propietaria o inquilina de varias; el acceso es a todas), por número.
  const apartmentsByUserId = useMemo(() => {
    const map = new Map();
    apartments.forEach((apt) => {
      const ids = new Set(
        [apt.owner, apt.ownerSecondary, apt.tenant, apt.tenantSecondary].filter((u) => u != null && u !== "").map(String)
      );
      ids.forEach((k) => {
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(apt);
      });
    });
    map.forEach((lista) =>
      lista.sort((a, b) => String(a.number ?? "").localeCompare(String(b.number ?? ""), "es", { numeric: true }))
    );
    return map;
  }, [apartments]);

  // Ocupante saliente: usuario con una tenencia CERRADA en el edificio. Mapea
  // userid -> número de la última unidad que ocupó (para mostrar "(saliente)").
  const formerUnitByUserId = useMemo(() => {
    const map = new Map();
    occupancies
      .filter((o) => o.active === false || o.dateTo)
      .sort((a, b) => String(a.dateTo || "").localeCompare(String(b.dateTo || "")))
      .forEach((o) => {
        const num =
          o.apartmentNumber ||
          apartments.find((a) => a.id === o.apartmentId)?.number ||
          null;
        if (num != null) map.set(String(o.userid), num);
      });
    return map;
  }, [occupancies, apartments]);

  // R5/T1: tenencia VIGENTE por usuario (para habilitar "Dar de baja"). Un
  // residente puede aparecer con más de una tenencia; nos quedamos con la activa.
  const activeOccupancyByUserId = useMemo(() => {
    const map = new Map();
    occupancies
      .filter((o) => o.active !== false && !o.dateTo)
      .forEach((o) => {
        if (o.userid != null) map.set(String(o.userid), o);
      });
    return map;
  }, [occupancies]);

  // Resuelve las unidades de un usuario para mostrar/ordenar. Devuelve
  // { numbers, number, suffix }: numbers son todas las que ocupa (por número),
  // number la primera (para ordenar) y suffix marca ocupante saliente o
  // asignación pendiente. Sin unidad asociada, numbers vacío y number null.
  const resolveUnit = useCallback(
    (row) => {
      const apts = apartmentsByUserId.get(String(row.userid)) || [];
      const numbers = apts.map((a) => a.number).filter((n) => n != null && n !== "");
      if (numbers.length) return { numbers, number: numbers[0], suffix: "" };
      const formerUnit = formerUnitByUserId.get(String(row.userid));
      if (formerUnit != null) return { numbers: [formerUnit], number: formerUnit, suffix: " (saliente)" };
      if (row.pendingApartmentId && row.accountState !== ACCOUNT_STATES.ACTIVE) {
        const pa = apartments.find((a) => a.id === row.pendingApartmentId);
        if (pa && pa.number) return { numbers: [pa.number], number: pa.number, suffix: " (pendiente)" };
      }
      return { numbers: [], number: null, suffix: "" };
    },
    [apartmentsByUserId, formerUnitByUserId, apartments]
  );

  // Fecha relevante de la columna "Última invitación": para cuentas activas es
  // la fecha de activación; para el resto, el último envío de invitación.
  const lastInvitationDate = useCallback((row) => {
    if (row.accountState === ACCOUNT_STATES.ACTIVE) {
      const at = row.activatedAt || row.onboardingCompletedAt;
      return at?.toDate ? at.toDate() : null;
    }
    const ts = row.lastInvitationSentAt || row.invitationSentAt;
    return ts?.toDate ? ts.toDate() : null;
  }, []);

  // -- Ordenamiento ----------------------------------------------------------
  const sortedUsers = useMemo(() => {
    if (!sortKey) return filteredUsers;
    const dir = sortDirection === "asc" ? 1 : -1;
    const valueFor = (row) => {
      switch (sortKey) {
        case "accountState":
          return ACCOUNT_STATE_LABELS[row.accountState] || "";
        case "NombreCompleto":
          return `${row.NombreCompleto || row.name || ""} ${row.ApellidosCompleto || ""}`
            .trim()
            .toLowerCase();
        case "unidad": {
          const n = resolveUnit(row).number;
          return n == null ? "" : String(n);
        }
        case "email":
          return String(row.email || "").toLowerCase();
        case "celular":
          return String(row.celular || "").toLowerCase();
        case "lastInvitationSentAt": {
          const d = lastInvitationDate(row);
          return d ? d.getTime() : 0;
        }
        default:
          return "";
      }
    };
    return [...filteredUsers].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      // Vacíos siempre al final, sin importar la dirección.
      if (va === "" && vb !== "") return 1;
      if (vb === "" && va !== "") return -1;
      return (
        String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" }) * dir
      );
    });
  }, [filteredUsers, sortKey, sortDirection, resolveUnit, lastInvitationDate]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  // -- Selection -------------------------------------------------------------
  const toggleSelect = (userid) => {
    const k = String(userid);
    const next = new Set(selectedIds);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredUsers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUsers.map((u) => String(u.userid))));
    }
  };

  const selectedUsers = useMemo(
    () => filteredUsers.filter((u) => selectedIds.has(String(u.userid))),
    [filteredUsers, selectedIds]
  );

  const selectionCounts = useMemo(() => {
    const c = { created: 0, expired: 0, invited: 0, active: 0, archived: 0 };
    selectedUsers.forEach((u) => {
      if (u.accountState === ACCOUNT_STATES.CREATED) c.created++;
      if (u.accountState === ACCOUNT_STATES.INVITED_EXPIRED) c.expired++;
      if (u.accountState === ACCOUNT_STATES.INVITED_ACTIVE) c.invited++;
      if (u.accountState === ACCOUNT_STATES.ACTIVE) c.active++;
      if (u.accountState === ACCOUNT_STATES.ARCHIVED) c.archived++;
    });
    return c;
  }, [selectedUsers]);

  // -- Acciones individuales (residentAccountAction) -------------------------
  const ocuparFila = (id, ocupada) => {
    const next = new Set(filasOcupadasRef.current);
    if (ocupada) next.add(id);
    else next.delete(id);
    filasOcupadasRef.current = next;
    setFilasOcupadas(next);
  };

  // Corre `fn` con el menú de la fila deshabilitado; ignora un segundo click
  // mientras la primera acción sigue en curso. `fn` arranca en el mismo gesto
  // del click (lo necesita el portapapeles de "Copiar link").
  const enFila = async (user, fn) => {
    if (!user?.id || filasOcupadasRef.current.has(user.id)) return;
    ocuparFila(user.id, true);
    try {
      await fn();
    } finally {
      ocuparFila(user.id, false);
    }
  };

  // Una sola acción sobre un residente; devuelve su resultado ({estado, motivo,
  // mensaje, ...}) o null si falló la llamada (ya avisado con un toast). Se
  // busca por userDocId por si el servidor informa más de una ficha.
  const accionSobre = async (user, accion, fallback) => {
    try {
      const res = await residentAccountAction({
        buildingId,
        accion,
        userDocIds: [user.id],
        requestId: nuevoRequestId(),
      });
      const lista = res?.resultados || [];
      return lista.find((r) => r?.userDocId === user.id) || lista[0] || { estado: "error", mensaje: fallback };
    } catch (err) {
      console.error(`[Cuentas] residentAccountAction ${accion} falló:`, codigoDeError(err));
      showToast(mensajeDeError(err, fallback), "error");
      return null;
    }
  };

  const avisarNoOk = (r, fallback) => {
    if (!r) return;
    // K2: no es un error del staff; queda para un superadmin (sin link ni aviso).
    if (r.estado === ESTADO_REVISION) {
      showToast(`${mensajeDe(r) || MSJ_REVISION} No se envió la invitación.`, "warning");
      return;
    }
    showToast(mensajeDe(r) || fallback, r.estado === "omitido" ? "warning" : "error");
  };

  // Copia al portapapeles el link que devuelve el callable (copiar_link emite
  // uno nuevo en cada llamada). No se guarda, no se loguea y no va a analytics.
  const handleCopyLink = (user) => {
    if (!esSuperadmin) {
      showToast(MSJ_LINK_SOLO_SUPERADMIN, "info");
      return undefined;
    }
    return enFila(user, async () => {
      const pedido = residentAccountAction({
        buildingId,
        accion: "copiar_link",
        userDocIds: [user.id],
      }).then((res) => {
        if (res?.activationLink) return { link: res.activationLink, vence: res.expiresAt };
        const r = (res?.resultados || [])[0];
        const err = new Error(mensajeDe(r) || "No se pudo generar el link.");
        err.estado = r?.estado || "error";
        err.mensaje = mensajeDe(r);
        throw err;
      });
      try {
        const copiado = await copiarAlPortapapeles(pedido.then((x) => x.link));
        const { link, vence } = await pedido;
        if (copiado) {
          const cuando = formatVence(vence);
          showToast(cuando ? `Link copiado (vence el ${cuando}).` : "Link copiado.", "success");
        } else {
          alertDialog("No pudimos copiarlo automáticamente. Copialo desde acá:", {
            title: "Link de activación",
            content: <LinkActivacion link={link} nombre={user.NombreCompleto} celular={user.celular} vence={vence} />,
          });
        }
      } catch (err) {
        if (err?.estado) {
          avisarNoOk({ estado: err.estado, mensaje: err.mensaje }, "No se pudo generar el link.");
        } else {
          console.error("[Cuentas] copiar_link falló:", codigoDeError(err));
          showToast(mensajeDeError(err, "No se pudo generar el link."), "error");
        }
      } finally {
        loadData();
      }
    });
  };

  // Reenviar (link vigente), renovar o primer envío ("Sin enviar"). Con una
  // invitación vencida o sin enviar, "reenviar" el servidor lo hace como renovar.
  const enviarInvitacion = async (user, accion, exito) => {
    let ofrecerLink = false;
    await enFila(user, async () => {
      const r = await accionSobre(user, accion, "No se pudo enviar la invitación.");
      if (r?.estado === "ok") {
        if (r.avisoOk) {
          const base = r.modo === "reenviar" ? "Invitación reenviada" : exito;
          showToast(`${base}${CANAL_TEXTO[r.canal] || ""}.`, "success");
        } else if (esSuperadmin) {
          ofrecerLink = true;
        } else {
          showToast(
            `No se pudo enviar: ${MSJ_SIN_SALIR.charAt(0).toLowerCase()}${MSJ_SIN_SALIR.slice(1)} Revisá el celular y el email de la ficha y reintentá.`,
            "warning"
          );
        }
      } else {
        avisarNoOk(r, "No se pudo enviar la invitación.");
      }
      loadData();
    });
    if (ofrecerLink) {
      const copiar = await confirmDialog(
        `${MSJ_SIN_SALIR} ¿Copiamos el link para que se lo mandes vos?`,
        { title: "No se pudo enviar", confirmLabel: "Copiar link", cancelLabel: "Cerrar" }
      );
      if (copiar) handleCopyLink(user);
    }
  };

  const handleResend = (user) => enviarInvitacion(user, "reenviar", "Invitación reenviada");
  const handleRenew = (user) => enviarInvitacion(user, "renovar", "Link renovado y enviado");
  const handleSendFirst = (user) => enviarInvitacion(user, "renovar", "Invitación enviada");

  const handleArchive = async (user) => {
    const ok = await confirmDialog(
      `¿Archivar a ${nombreDe(user)}? La cuenta quedará oculta y no podrá iniciar sesión.`,
      { title: "Archivar cuenta", confirmLabel: "Archivar", danger: true }
    );
    if (!ok) return;
    await enFila(user, async () => {
      const r = await accionSobre(user, "archivar", "No se pudo archivar.");
      if (r?.estado === "ok") showToast("Cuenta archivada", "success");
      else avisarNoOk(r, "No se pudo archivar.");
      loadData();
    });
  };

  // Al reactivar una invitación queda "Sin enviar" (los links se revocaron al
  // archivar): se ofrece mandar una nueva.
  const handleUnarchive = async (user) => {
    let ofrecerEnvio = false;
    await enFila(user, async () => {
      const r = await accionSobre(user, "reactivar", "No se pudo reactivar.");
      if (r?.estado === "ok") {
        showToast("Cuenta reactivada", "success");
        ofrecerEnvio = r.estadoCuenta === ACCOUNT_STATES.CREATED;
      } else {
        avisarNoOk(r, "No se pudo reactivar.");
      }
      loadData();
    });
    if (ofrecerEnvio) {
      const enviar = await confirmDialog(
        "Quedó «Sin enviar»: los links de invitación anteriores se anularon al archivarla. ¿Le mandamos una invitación nueva ahora?",
        { title: "Cuenta reactivada", confirmLabel: "Enviar invitación", cancelLabel: "Más tarde" }
      );
      if (enviar) handleSendFirst(user);
    }
  };

  // D3, solo superadmin: una cuenta activada con un link en claro, ya
  // verificada, deja de mandar sus invitaciones y asignaciones a revisión. De
  // a una por vez a propósito: cada cuenta se revisa antes de marcarla.
  const handleMarcarRevisada = async (user) => {
    if (!esSuperadmin) return;
    const ok = await confirmDialog(
      `${nombreDe(user)} activó su cuenta con un link de invitación viejo, sin constancia del servidor. Marcala como revisada solo si confirmaste que la cuenta es de esa persona: desde ahí sus invitaciones y asignaciones dejan de pasar por un superadmin. Queda registrado con tu usuario.`,
      { title: "Marcar cuenta revisada", confirmLabel: "Marcar revisada" }
    );
    if (!ok) return;
    await enFila(user, async () => {
      const r = await accionSobre(user, "marcar_revisada", "No se pudo marcar la cuenta como revisada.");
      if (r?.estado === "ok") showToast("Cuenta marcada como revisada.", "success");
      else avisarNoOk(r, "No se pudo marcar la cuenta como revisada.");
      loadData();
    });
  };

  // R5/T1: baja de residente sin reemplazo. Abre el modal con la fecha de hoy
  // (Montevideo).
  const openBaja = (user, occ) => {
    setBajaTarget({ occ, user });
    setBajaDate(hoyMontevideo());
  };

  const closeBaja = () => {
    if (savingBaja) return;
    setBajaTarget(null);
    setBajaDate("");
  };

  const handleBaja = async () => {
    if (savingBaja) return;
    if (!bajaTarget || !bajaDate) {
      showToast("Elegí la fecha de baja", "warning");
      return;
    }
    const { occ, user } = bajaTarget;
    const ok = await confirmDialog(
      `¿Dar de baja a ${nombreDe(user)} el ${bajaDate}? A partir de esa fecha la unidad queda sin ${
        occ.role === "owner" ? "propietario" : "inquilino"
      } y el cierre del mes prorratea los gastos comunes (los días ocupados al saliente, el resto al propietario). El saliente conserva acceso de solo lectura a su histórico.`,
      { title: "Dar de baja residente", confirmLabel: "Dar de baja", danger: true }
    );
    if (!ok) return;
    setSavingBaja(true);
    try {
      const datos = { op: "baja", occupancyDocId: occ.id, dateTo: bajaDate };
      const r = await llamarConfirmando(
        occupancyAction,
        { ...datos, requestId: bajaIntento(JSON.stringify(datos)) },
        confirmDialog
      );
      if (r.cancelado) return;
      terminarBajaIntento();
      showToast(r.res?.sinCambios ? "Esa baja ya estaba registrada." : "Residente dado de baja.", "success");
      setBajaTarget(null);
      setBajaDate("");
      loadData();
    } catch (err) {
      console.error("[Cuentas] occupancyAction baja falló:", codigoDeError(err));
      showToast(mensajeDeError(err, "Error al dar de baja."), "error");
    } finally {
      setSavingBaja(false);
    }
  };

  // -- Acciones masivas -----------------------------------------------------
  // En lotes de 50; un lote que falla entero cuenta como fallido para cada uno.
  const accionMasiva = async (accion, destinos) => {
    const resultados = [];
    for (const lote of enLotes(destinos)) {
      try {
        const res = await residentAccountAction({
          buildingId,
          accion,
          userDocIds: lote.map((u) => u.id),
          requestId: nuevoRequestId(),
        });
        resultados.push(...(res?.resultados || []));
      } catch (err) {
        console.error(`[Cuentas] residentAccountAction ${accion} (lote) falló:`, codigoDeError(err));
        const mensaje = mensajeDeError(err);
        lote.forEach((u) => resultados.push({ userDocId: u.id, estado: "error", mensaje }));
      }
    }
    return resultados;
  };

  // Detalle de lo que no salió en una acción masiva (quién y por qué).
  const mostrarProblemas = (titulo, resultados, destinos) => {
    const porId = new Map(destinos.map((u) => [u.id, u]));
    const problemas = resultados
      .filter(
        (r) =>
          r.estado === "error" ||
          r.estado === ESTADO_REVISION ||
          (r.estado === "ok" && r.avisoOk === false) ||
          (r.estado === "omitido" && !OMITIDOS_INOCUOS.has(r.motivo))
      )
      .map((r) => ({
        id: r.userDocId,
        nombre: porId.has(r.userDocId) ? nombreDe(porId.get(r.userDocId)) : r.userDocId,
        mensaje:
          r.estado === "ok"
            ? MSJ_SIN_SALIR
            : r.estado === ESTADO_REVISION
              ? `${mensajeDe(r) || MSJ_REVISION} No se envió la invitación.`
              : mensajeDe(r) || "No se pudo completar la acción.",
      }));
    if (!problemas.length) return;
    alertDialog(
      problemas.length === 1 ? "Una cuenta necesita atención:" : `${problemas.length} cuentas necesitan atención:`,
      {
        title: titulo,
        wide: true,
        content: (
          <ul style={{ margin: 0, paddingLeft: spacing[5], display: "flex", flexDirection: "column", gap: spacing[2] }}>
            {problemas.map((p) => (
              <li key={p.id} style={{ fontSize: typography.sizes.base, color: colors.gray900, lineHeight: 1.5 }}>
                {p.nombre}
                <span style={{ color: colors.gray600 }}>{` — ${p.mensaje}`}</span>
              </li>
            ))}
          </ul>
        ),
      }
    );
  };

  // Enviar o reenviar en un solo pedido: el servidor decide por destino si
  // reenvía el link vigente o emite uno nuevo (Sin enviar o Vencida). Las
  // fichas que crea la carga masiva quedan «Sin enviar» y salen desde acá.
  const handleBulkRenew = async () => {
    const destinos = selectedUsers.filter((u) => ESTADOS_ENVIO_MASIVO.has(u.accountState));
    if (destinos.length === 0) {
      showToast("No hay invitaciones seleccionadas para enviar", "warning");
      return;
    }
    setBulkProcessing(true);
    try {
      const resultados = await accionMasiva("reenviar", destinos);
      const enviadas = resultados.filter((r) => r.estado === "ok" && r.avisoOk).length;
      const omitidas = resultados.filter((r) => r.estado === "omitido").length;
      const fallidas = resultados.filter(
        (r) => r.estado === "error" || (r.estado === "ok" && !r.avisoOk)
      ).length;
      // K2: las fichas que quedan para un superadmin se cuentan aparte.
      const enRevision = resultados.filter((r) => r.estado === ESTADO_REVISION).length;
      showToast(
        `Enviadas: ${enviadas}. Omitidas: ${omitidas}. Fallidas: ${fallidas}.${
          enRevision ? ` En revisión de un superadmin: ${enRevision}.` : ""
        }`,
        enviadas > 0 ? "success" : fallidas > 0 ? "error" : "warning"
      );
      mostrarProblemas("Enviar / reenviar invitación", resultados, destinos);
    } finally {
      setBulkProcessing(false);
      setSelectedIds(new Set());
      loadData();
    }
  };

  const handleBulkArchive = async () => {
    const destinos = selectedUsers.filter((u) => u.accountState !== ACCOUNT_STATES.ARCHIVED);
    if (destinos.length === 0) {
      showToast("Las cuentas seleccionadas ya están archivadas", "info");
      return;
    }
    const ok = await confirmDialog(
      `¿Archivar ${destinos.length} cuenta(s)? Quedarán ocultas y no podrán iniciar sesión.`,
      { title: "Archivar cuentas", confirmLabel: "Archivar", danger: true }
    );
    if (!ok) return;
    setBulkProcessing(true);
    try {
      const resultados = await accionMasiva("archivar", destinos);
      const archivadas = resultados.filter((r) => r.estado === "ok").length;
      const fallidas = resultados.filter((r) => r.estado === "error").length;
      showToast(
        `${archivadas} cuenta(s) archivada(s)${fallidas ? `. Fallidas: ${fallidas}.` : ""}`,
        archivadas > 0 ? "success" : fallidas > 0 ? "error" : "info"
      );
      mostrarProblemas("Archivar cuentas", resultados, destinos);
    } finally {
      setBulkProcessing(false);
      setSelectedIds(new Set());
      loadData();
    }
  };

  // Avisos después de "Editar datos" (el modal ya se cerró).
  const avisarEdicion = async (user, { contacto, fecha } = {}) => {
    const huboCambios = (contacto && !contacto.sinCambios) || (fecha && !fecha.sinCambios);
    showToast(huboCambios ? "Datos actualizados" : "No había cambios para guardar.", huboCambios ? "success" : "info");
    const notas = [];
    if (contacto?.authSincronizada) {
      notas.push(
        `Cambió el email con el que inicia sesión${contacto.sesionesRevocadas ? " y se cerraron sus sesiones" : ""}.` +
          (contacto.avisoOk === false ? " No pudimos avisarle al email anterior." : "")
      );
    }
    if (contacto?.cuentaExistente) {
      notas.push("Ese email ya tiene cuenta en Habitia: la ficha quedó asociada a esa cuenta.");
    }
    if (contacto?.celularCompartido) notas.push("El celular queda compartido con otra ficha.");
    if (notas.length) showToast(notas.join(" "), "info");
    // Revoca con email, celular o documento cambiados (updateResidentContact):
    // el texto no nombra ninguno.
    if (contacto?.invitacionRevocada) {
      const enviar = await confirmDialog(
        "Cambiaste datos de contacto o el documento de una invitación pendiente: los links anteriores ya no sirven. ¿Le mandamos una invitación nueva?",
        { title: "Reenviar invitación", confirmLabel: "Enviar invitación", cancelLabel: "Más tarde" }
      );
      if (enviar) handleSendFirst(user);
    }
  };

  // -- Columnas DataTable ---------------------------------------------------
  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          aria-label="Seleccionar todos"
          checked={selectedIds.size > 0 && selectedIds.size === filteredUsers.length}
          onChange={toggleSelectAll}
        />
      ),
      width: "40px",
      render: (_, row) => (
        <input
          type="checkbox"
          aria-label={`Seleccionar ${row.NombreCompleto}`}
          checked={selectedIds.has(String(row.userid))}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelect(row.userid);
          }}
        />
      ),
    },
    {
      key: "accountState",
      label: "Estado",
      width: "140px",
      sortable: true,
      render: (val, row) => {
        const estado = <StatusBadge status={ACCOUNT_STATE_BADGE_KEYS[val]} />;
        // D3: solo el superadmin ve qué cuentas se activaron con un link en claro.
        if (!esSuperadmin || !activadaConLinkEnClaro(row)) return estado;
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: spacing[1] }}>
            {estado}
            <span title={MSJ_LINK_EN_CLARO}>
              <StatusBadge status="atencion" label="Revisar activación" size="sm" />
            </span>
          </div>
        );
      },
    },
    {
      key: "NombreCompleto",
      label: "Nombre",
      sortable: true,
      render: (val, row) =>
        `${val || row.name || ""} ${row.ApellidosCompleto || ""}`.trim() || "—",
    },
    {
      key: "unidad",
      label: "Unidad",
      width: "120px",
      sortable: true,
      render: (_, row) => {
        const { numbers, suffix } = resolveUnit(row);
        if (!numbers.length) return "—";
        // Muchas unidades (un desarrollador, un inversor): las primeras y "+N"; todas en el tooltip.
        const todas = numbers.join(", ");
        if (numbers.length <= MAX_UNIDADES_VISIBLES) return `${todas}${suffix}`;
        return (
          <span title={todas} aria-label={`Unidades ${todas}`}>
            {numbers.slice(0, MAX_UNIDADES_VISIBLES - 1).join(", ")} +{numbers.length - MAX_UNIDADES_VISIBLES + 1}
          </span>
        );
      },
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      render: (val) => val || "—",
    },
    {
      key: "celular",
      label: "Celular",
      width: "120px",
      sortable: true,
      render: (val) => val || "—",
    },
    {
      // Fase 1.4 — canales por los que el residente es contactable. Hoy muestra
      // disponibilidad (email/WhatsApp) + si la invitación ya salió; los estados
      // entregado/leído se sumarán cuando WhatsApp entregue de verdad (billing
      // Meta + templates aprobados) leyendo `messages`.
      key: "canales",
      label: "Canales",
      width: "90px",
      render: (_, row) => {
        const sent = !!(row.lastInvitationSentAt || row.invitationSentAt);
        const chip = (ok, Icon, title) => (
          <span
            title={title}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 24,
              borderRadius: 6,
              background: !ok ? colors.gray100 : sent ? colors.primaryLighter : colors.white,
              color: !ok ? colors.gray300 : sent ? colors.primary : colors.gray500,
              border: `1px solid ${!ok ? colors.gray200 : sent ? colors.primary : colors.gray300}`,
            }}
          >
            <Icon size={12} />
          </span>
        );
        return (
          <div style={{ display: "flex", gap: 4 }}>
            {chip(
              !!row.email,
              FaEnvelope,
              row.email ? `Email: ${row.email}${sent ? " · invitación enviada" : " · sin enviar"}` : "Sin email en la ficha"
            )}
            {chip(
              !!row.celular,
              FaWhatsapp,
              row.celular ? `WhatsApp: ${row.celular}${sent ? " · invitación enviada" : " · sin enviar"}` : "Sin celular en la ficha"
            )}
          </div>
        );
      },
    },
    {
      key: "lastInvitationSentAt",
      label: "Última invitación",
      width: "160px",
      sortable: true,
      render: (_, row) => {
        const isActive = row.accountState === ACCOUNT_STATES.ACTIVE;
        const d = lastInvitationDate(row);
        if (!d) return isActive ? "activó" : "—";
        return (
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
            <span style={{ color: colors.gray900 }}>{formatDate(d)}</span>
            <span style={{ fontSize: typography.sizes.xs, color: colors.gray600 }}>
              {isActive ? "activó " : ""}
              {formatRelative(d)}
            </span>
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "",
      width: "180px",
      align: "right",
      render: (_, row) => (
        <RowActions
          row={row}
          activeOccupancy={activeOccupancyByUserId.get(String(row.userid))}
          esSuperadmin={esSuperadmin}
          busy={filasOcupadas.has(row.id)}
          onEdit={setEditUser}
          onResend={handleResend}
          onRenew={handleRenew}
          onSendFirst={handleSendFirst}
          onCopy={handleCopyLink}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
          onBaja={openBaja}
          onMarcarRevisada={handleMarcarRevisada}
        />
      ),
    },
  ];

  // -- Render --------------------------------------------------------------
  if (!selectedBuilding) {
    return (
      <div style={pageContainer}>
        <EmptyState
          icon={FaUserPlus}
          title="Seleccioná un edificio"
          description="Elegí un edificio del menú superior para ver sus cuentas."
        />
      </div>
    );
  }

  return (
    <div style={pageContainer}>
      <ScreenHeader
        icon={FaUserPlus}
        title="Residentes"
        subtitle={selectedBuilding?.name}
      />

      {/* KPIs */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: spacing[4],
          marginBottom: spacing[8],
        }}
      >
        {KPI_DEFS.map((kpi) => (
          <KPICard
            key={kpi.state}
            icon={kpi.icon}
            iconBg={kpi.iconBg}
            iconColor={kpi.iconColor}
            label={kpi.label}
            value={kpis[kpi.state] || 0}
            onClick={() => setFilterState(filterState === kpi.state ? "all" : kpi.state)}
          />
        ))}
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: spacing[3],
          marginBottom: spacing[4],
          flexWrap: "wrap",
        }}
      >
        <div style={{ position: "relative", flex: "1 1 280px", minWidth: 240 }}>
          <FaSearch
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: colors.gray400,
              fontSize: 13,
            }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email, celular o documento"
            className="habitia-input"
            style={{ ...inputBase, paddingLeft: 36, width: "100%" }}
          />
        </div>

        <select
          value={filterState}
          onChange={(e) => setFilterState(e.target.value)}
          style={{ ...inputBase, width: 200 }}
          aria-label="Filtrar por estado"
        >
          <option value="all">Todos los estados</option>
          {Object.entries(ACCOUNT_STATE_LABELS).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setShowInviteModal(true)}
          className="habitia-btn-primary"
          style={btnPrimary}
        >
          <FaPlus /> Invitar nuevo
        </button>
      </div>

      {/* Acciones masivas */}
      {selectedIds.size > 0 && (
        <div
          style={{
            background: colors.primaryLighter,
            border: `1px solid ${colors.primaryLight}`,
            borderRadius: radius.sm,
            padding: spacing[3],
            display: "flex",
            alignItems: "center",
            gap: spacing[3],
            marginBottom: spacing[4],
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: colors.gray900, fontSize: 14 }}>
            {selectedIds.size} seleccionada(s)
          </span>
          {selectionCounts.created + selectionCounts.expired + selectionCounts.invited > 0 && (
            <button
              type="button"
              onClick={handleBulkRenew}
              disabled={bulkProcessing}
              style={btnSecondary}
              className="habitia-btn-secondary"
            >
              <FaRedo />{" "}
              {bulkProcessing
                ? "Procesando…"
                : `Enviar / reenviar invitación (${
                    selectionCounts.created + selectionCounts.expired + selectionCounts.invited
                  })`}
            </button>
          )}
          <button
            type="button"
            onClick={handleBulkArchive}
            disabled={bulkProcessing}
            style={btnSecondary}
            className="habitia-btn-secondary"
          >
            <FaArchive /> Archivar
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            disabled={bulkProcessing}
            style={btnGhost}
            className="habitia-btn-ghost"
          >
            Limpiar
          </button>
        </div>
      )}

      {/* Tabla */}
      {loading ? (
        <p style={{ color: colors.gray600, fontSize: 14 }}>Cargando...</p>
      ) : (
        <DataTable
          columns={columns}
          data={sortedUsers}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          emptyIcon={FaSearch}
          emptyTitle="Sin cuentas en este edificio"
          emptyDescription="Invitá al primer residente con el botón 'Invitar nuevo'."
        />
      )}

      {/* Modal invitar */}
      {showInviteModal && (
        <InviteModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            loadData();
          }}
          building={selectedBuilding}
          apartments={apartments}
        />
      )}

      {/* Modal editar datos */}
      {editUser && (
        <EditModal
          user={editUser}
          buildingId={buildingId}
          esSuperadmin={esSuperadmin}
          activeOccupancy={
            occupancies.find(
              (o) => String(o.userid) === String(editUser.userid) && o.active !== false
            ) || null
          }
          onClose={() => setEditUser(null)}
          onRefrescar={loadData}
          onSuccess={(resultado) => {
            const editado = editUser;
            setEditUser(null);
            loadData();
            avisarEdicion(editado, resultado);
          }}
        />
      )}

      {/* R5/T1: Modal Dar de baja residente (sin reemplazo) */}
      {bajaTarget && (
        <AdminModal
          open={true}
          onClose={closeBaja}
          title="Dar de baja residente"
          subtitle={
            `${bajaTarget.user.NombreCompleto || bajaTarget.user.name || ""} ${bajaTarget.user.ApellidosCompleto || ""}`.trim() ||
            undefined
          }
          size="narrow"
          icon={<FaUserSlash />}
          footer={
            <>
              <button
                type="button"
                onClick={closeBaja}
                disabled={savingBaja}
                style={btnSecondary}
                className="habitia-btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleBaja}
                disabled={savingBaja}
                style={btnDanger}
                className="habitia-btn-primary"
              >
                {savingBaja ? "Procesando…" : "Dar de baja"}
              </button>
            </>
          }
        >
          <div>
            <p style={{ fontSize: typography.sizes.base, color: colors.gray900, marginBottom: spacing[2] }}>
              {`${bajaTarget.user.NombreCompleto || bajaTarget.user.name || ""}`.trim() || `ID ${bajaTarget.user.userid}`}
              {" — "}
              {bajaTarget.occ.role === "owner" ? "Propietario" : "Inquilino"}
            </p>
            <FormField label="Último día de responsabilidad" htmlFor="baja-fecha">
              <input
                id="baja-fecha"
                type="date"
                value={bajaDate}
                onChange={(e) => setBajaDate(e.target.value)}
                disabled={savingBaja}
                style={inputBase}
                className="habitia-input"
              />
            </FormField>
            <p style={{ fontSize: typography.sizes.sm, color: colors.gray500, marginTop: spacing[2], lineHeight: 1.5 }}>
              Desde el día siguiente, la unidad queda sin este ocupante y no entra reemplazo. El cierre del mes prorratea los gastos comunes por días (los ocupados al saliente, el resto al propietario). El saliente conserva acceso de solo lectura a su histórico.
            </p>
          </div>
        </AdminModal>
      )}
    </div>
  );
};

// =============================================================================
// Componentes internos
// =============================================================================

const RowActions = ({
  row,
  activeOccupancy,
  esSuperadmin,
  busy,
  onEdit,
  onResend,
  onRenew,
  onSendFirst,
  onCopy,
  onArchive,
  onUnarchive,
  onBaja,
  onMarcarRevisada,
}) => {
  const [open, setOpen] = useState(false);
  const state = row.accountState;

  // Cierra el menú si hace click fuera.
  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    setTimeout(() => document.addEventListener("click", handler, { once: true }), 0);
    return () => document.removeEventListener("click", handler);
  }, [open]);

  const actions = [];
  actions.push({ label: "Editar datos", icon: FaPen, onClick: () => onEdit(row) });
  // D3: solo superadmin y solo en cuentas activadas con un link en claro (al
  // staff no se le muestra: no la puede hacer).
  if (esSuperadmin && activadaConLinkEnClaro(row) && onMarcarRevisada) {
    actions.push({ label: "Marcar revisada", icon: FaUserCheck, onClick: () => onMarcarRevisada(row) });
  }
  if (state === ACCOUNT_STATES.INVITED_ACTIVE) {
    actions.push({ label: "Reenviar invitación", icon: FaEnvelope, onClick: () => onResend(row) });
    actions.push({ label: "Renovar y reenviar", icon: FaRedo, onClick: () => onRenew(row) });
    // Hasta las reglas 0D el servidor le da el link solo a un superadmin: para
    // el resto queda deshabilitado y el click explica por qué.
    actions.push(
      esSuperadmin
        ? { label: "Copiar link", icon: FaCopy, onClick: () => onCopy(row) }
        : {
            label: "Copiar link",
            icon: FaCopy,
            disabled: true,
            hint: "Solo superadmin, por ahora",
            title: MSJ_LINK_SOLO_SUPERADMIN,
            onClick: () => onCopy(row),
          }
    );
  }
  if (state === ACCOUNT_STATES.INVITED_EXPIRED) {
    actions.push({ label: "Renovar y reenviar", icon: FaRedo, onClick: () => onRenew(row) });
  }
  if (state === ACCOUNT_STATES.CREATED) {
    actions.push({ label: "Enviar invitación", icon: FaEnvelope, onClick: () => onSendFirst(row) });
  }
  if (state === ACCOUNT_STATES.ARCHIVED) {
    actions.push({ label: "Reactivar", icon: FaRedo, onClick: () => onUnarchive(row) });
  } else {
    actions.push({ label: "Archivar", icon: FaArchive, onClick: () => onArchive(row) });
  }
  // R5/T1: dar de baja solo si el residente tiene una tenencia vigente de
  // INQUILINO (se va y no entra reemplazo). Un propietario NO se puede dar de
  // baja sin reemplazo — toda unidad debe tener propietario responsable de GC;
  // el cambio se hace por "Invitar residente" como Propietario. Acción destructiva.
  if (activeOccupancy && activeOccupancy.role !== "owner" && onBaja && state !== ACCOUNT_STATES.ARCHIVED) {
    actions.push({ label: "Dar de baja", icon: FaUserSlash, danger: true, onClick: () => onBaja(row, activeOccupancy) });
  }

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        disabled={busy}
        aria-busy={busy || undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="habitia-btn-ghost"
        aria-label={busy ? "Procesando acción" : "Acciones"}
        style={{
          background: "transparent",
          border: "none",
          padding: "6px 10px",
          borderRadius: radius.sm,
          cursor: busy ? "progress" : "pointer",
          opacity: busy ? 0.5 : 1,
          color: colors.gray600,
          fontSize: 14,
        }}
      >
        <FaEllipsisV />
      </button>
      {open && !busy && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            right: 0,
            top: "100%",
            background: colors.white,
            borderRadius: radius.sm,
            boxShadow: shadows.lg,
            minWidth: 200,
            zIndex: 50,
            padding: 4,
          }}
        >
          {actions.map((a, i) => {
            const color = a.disabled ? colors.gray400 : a.danger ? colors.error : colors.gray900;
            const iconColor = a.disabled ? colors.gray300 : a.danger ? colors.error : colors.gray600;
            return (
              <button
                key={i}
                type="button"
                aria-disabled={a.disabled || undefined}
                title={a.title}
                onClick={() => {
                  setOpen(false);
                  a.onClick();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  padding: "8px 10px",
                  borderRadius: radius.sm,
                  color,
                  fontSize: 13,
                  cursor: a.disabled ? "not-allowed" : "pointer",
                  textAlign: "left",
                  fontFamily: typography.fontFamily,
                  fontWeight: 400,
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = colors.gray50)}
                onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <a.icon style={{ fontSize: 12, color: iconColor, flexShrink: 0 }} />
                <span style={{ display: "flex", flexDirection: "column" }}>
                  {a.label}
                  {a.hint && (
                    <span style={{ fontSize: typography.sizes.xs, color: colors.gray400 }}>{a.hint}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const InviteModal = ({ onClose, onSuccess, building, apartments }) => {
  const { showToast } = useToast();
  const { confirmDialog, alertDialog } = useAppDialog();
  const [submitting, setSubmitting] = useState(false);
  const enviandoRef = useRef(false);
  const [intentoId] = useIntentoId();
  const [form, setForm] = useState(() => ({
    nombre: "",
    apellidos: "",
    email: "",
    celular: "",
    documento: "",
    tipoDocumento: "CI",
    apartmentId: "",
    rol: "Propietario",
    // Hoy en Montevideo (el mismo default que usa el servidor).
    fechaIngreso: hoyMontevideo(),
  }));
  const [sendInvitationEmail, setSendInvitationEmail] = useState(true);
  const [error, setError] = useState("");
  // K2: la invitación quedó para un superadmin (mensaje del servidor).
  const [revision, setRevision] = useState("");

  // Búsqueda de la persona en el servidor (inviteResident, modo "preview"),
  // con autocompletado. status: idle | searching | none | found | otherRole | conflict
  const [lookup, setLookup] = useState({ status: "idle", result: null });
  const [previewNonce, setPreviewNonce] = useState(0); // fuerza otra búsqueda
  const lookupSeq = useRef(0); // descarta respuestas fuera de orden
  const lastLookupKey = useRef(""); // evita repetir la misma query
  const prefilledFrom = useRef(null); // userDocId ya usado para precargar el nombre (no re-pisar)

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // Una unidad del edificio que se alquila solo admite inquilinos.
      if (name === "apartmentId") {
        const roles = rolesParaUnidad(apartments.find((a) => a.id === value));
        if (!roles.some((r) => r.label === next.rol)) next.rol = roles[0].label;
      }
      return next;
    });
    // Con otros datos, el aviso de revisión anterior ya no aplica.
    setRevision("");
  };
  const unidadElegida = apartments.find((a) => a.id === form.apartmentId) || null;
  const rolesDisponibles = rolesParaUnidad(unidadElegida);

  // Mientras se envía (o hay una confirmación abierta) Escape y el overlay no
  // cierran el modal: el alta seguiría corriendo sin pantalla.
  const cerrar = () => {
    if (!submitting && !enviandoRef.current) onClose();
  };

  // Al completar email o documento (debounce), buscamos si la persona ya existe.
  // De una persona de un edificio del staff se precargan SOLO nombre y
  // apellidos (si están vacíos). Email, celular y documento los tipea siempre el
  // staff: son los datos a los que sale el link y con los que se activa, y una
  // ficha cargada por otro (p. ej. una forjada con el email de un vecino) no
  // puede dictarlos. En una ficha pendiente, lo tipeado la corrige al invitar
  // (planPersona en el servidor).
  useEffect(() => {
    const email = String(form.email || "").trim();
    const documento = String(form.documento || "").trim();
    const emailOk = isValidEmail(email);
    // Un documento con formato inválido haría fallar la búsqueda entera.
    const docOk = !!documento && !getDocumentoValidationMessage(form.tipoDocumento, documento);
    if ((!emailOk && !docOk) || building?.buildingid == null) {
      setLookup({ status: "idle", result: null });
      lastLookupKey.current = "";
      return undefined;
    }
    const docNorm = docOk ? normalizeDocumento(documento, form.tipoDocumento) : "";
    const key = `${emailOk ? email.toLowerCase() : ""}|${docNorm}|${form.tipoDocumento}`;
    if (key === lastLookupKey.current) return undefined;

    const t = setTimeout(async () => {
      const seq = ++lookupSeq.current;
      setLookup((l) => ({ ...l, status: "searching" }));
      try {
        const result = await inviteResident({
          modo: "preview",
          buildingId: building.buildingid,
          residente: {
            ...(emailOk ? { email } : {}),
            ...(docOk ? { documento } : {}),
            tipoDocumento: form.tipoDocumento,
          },
        });
        if (seq !== lookupSeq.current) return; // llegó tarde, hay otra en vuelo
        lastLookupKey.current = key;
        const status = ESTADO_PREVIEW[result?.estado] || "none";
        setLookup({ status, result });

        // Precarga solo con datos completos (persona de un edificio del staff) y
        // solo del nombre: aunque el servidor mande más, email, celular,
        // documento y tipo de documento no se completan.
        const src = result?.precarga;
        if (
          status === "found" &&
          result.precargaCompleta === true &&
          src &&
          prefilledFrom.current !== result.userDocId
        ) {
          prefilledFrom.current = result.userDocId;
          setForm((f) => ({
            ...f,
            nombre: f.nombre || src.nombre || "",
            apellidos: f.apellidos || src.apellidos || "",
          }));
        }
      } catch (err) {
        // No bloquea: el commit repite el dedupe en el servidor.
        if (seq === lookupSeq.current) setLookup({ status: "idle", result: null });
        console.warn("[InviteModal] búsqueda de existente falló (no bloquea):", codigoDeError(err));
      }
    }, 600);
    return () => clearTimeout(t);
  }, [form.email, form.documento, form.tipoDocumento, previewNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enviandoRef.current) return;
    setError("");
    setRevision("");
    const residente = {
      nombre: form.nombre.trim(),
      apellidos: form.apellidos.trim(),
      email: form.email.trim(),
      celular: form.celular.trim(),
      documento: form.documento.trim(),
      tipoDocumento: form.tipoDocumento,
    };
    if (
      !residente.nombre ||
      !residente.apellidos ||
      !residente.email ||
      !residente.celular ||
      !residente.documento ||
      !form.apartmentId
    ) {
      setError("Completá todos los campos.");
      return;
    }

    // Formato del documento según tipo (Pasaporte/Otro aceptan letras).
    const docMsg = getDocumentoValidationMessage(form.tipoDocumento, residente.documento);
    if (docMsg) {
      setError(docMsg);
      return;
    }

    // Email y documento de personas distintas: no se puede invitar así.
    if (lookup.status === "conflict") {
      setError("El email y el documento pertenecen a personas distintas. Corregí uno de los dos.");
      return;
    }

    enviandoRef.current = true;
    try {
      // Dígito verificador de la cédula uruguaya — advertencia, no bloquea
      // (hay CIs viejas o documentos extranjeros cargados como CI).
      if (form.tipoDocumento === "CI") {
        const ci = validateCIUY(residente.documento);
        if (!ci.valid) {
          const ok = await confirmDialog(`${ci.message} ¿Continuar igual?`, {
            title: "Verificá la cédula",
            confirmLabel: "Continuar igual",
            danger: true,
          });
          if (!ok) return;
        }
      }

      // Aviso: si el email es el del propio administrador logueado, NO se crea un
      // residente nuevo (el sistema reconoce tu cuenta y solo te asigna a la
      // unidad). Suele ser un error al cargar un residente de prueba.
      const adminEmail = String(auth.currentUser?.email || "").trim().toLowerCase();
      if (adminEmail && adminEmail === residente.email.toLowerCase()) {
        const ok = await confirmDialog(
          "Este es tu propio email de administrador. No se va a crear un residente nuevo: quedarías vos asignado a la unidad. Para dar de alta a otra persona, usá su email real. ¿Continuar igual?",
          { title: "Email de administrador", confirmLabel: "Continuar igual", danger: true }
        );
        if (!ok) return;
      }

      const rolOption = rolesDisponibles.find((o) => o.label === form.rol) || rolesDisponibles[0];
      const datos = {
        modo: "commit",
        buildingId: building?.buildingid,
        apartmentId: form.apartmentId,
        slot: rolOption.field,
        residente,
        enviarAviso: sendInvitationEmail,
        ...(form.fechaIngreso ? { fechaIngreso: form.fechaIngreso } : {}),
      };

      setSubmitting(true);
      // El servidor pide confirmar, en orden: misma persona (coincidencia por
      // documento) → lugar ocupado (reemplaza al "Slot ocupado" del cliente) →
      // mes con cierre. Cada reintento va con el mismo requestId.
      let respuesta;
      try {
        respuesta = await llamarConfirmando(
          inviteResident,
          { ...datos, requestId: intentoId(JSON.stringify(datos)) },
          confirmDialog
        );
      } catch (err) {
        const motivo = err?.details?.motivo;
        if (codigoDeError(err) === "aborted" && (motivo === "persona_cambio" || motivo === "persona_recien_creada")) {
          // Alguien cambió o cargó a esta persona mientras tanto: volver a buscarla.
          lastLookupKey.current = "";
          setPreviewNonce((n) => n + 1);
        }
        console.error("[InviteModal] inviteResident falló:", codigoDeError(err), motivo || "");
        setError(mensajeDeError(err, "No se pudo procesar la invitación"));
        return;
      }
      if (respuesta.cancelado) return;

      const res = respuesta.res || {};
      // K2 (R1'): la persona ya tiene una ficha vinculada a otro edificio que
      // no administrás. El servidor no asignó la unidad ni emitió ni mandó el
      // link: la revisa un superadmin. Nada cambió, así que no se recarga; el
      // modal queda abierto con el aviso (si fue un email equivocado, se corrige).
      if (res.outcome === INVITE_OUTCOMES.REVISION_SUPERADMIN) {
        setRevision(mensajeDe(res) || MSJ_REVISION);
        return;
      }
      const conAviso = {
        [INVITE_OUTCOMES.CREATED]: "Residente invitado",
        [INVITE_OUTCOMES.REINVITED]: "Encontramos al residente y le reenviamos la invitación",
        [INVITE_OUTCOMES.ASSIGNED_EXISTING]: "Ya tenía cuenta activa: lo asignamos a esta unidad",
      };
      // Si el admin eligió NO enviar invitación, se crea/asigna sin avisar.
      const sinAviso = {
        [INVITE_OUTCOMES.CREATED]: "Residente creado sin enviar invitación.",
        [INVITE_OUTCOMES.REINVITED]: "Residente actualizado sin enviar invitación.",
        [INVITE_OUTCOMES.ASSIGNED_EXISTING]: "Residente asignado a esta unidad (ya tenía cuenta).",
      };
      const mensajes = res.aviso === "omitido" ? sinAviso : conAviso;
      const notas = [];
      if (res.aviso === "fallido" && !res.activationLink) {
        notas.push(
          res.outcome === INVITE_OUTCOMES.ASSIGNED_EXISTING || res.cuentaExistente
            ? "No pudimos avisarle: avisale vos, su cuenta ya está activa."
            : "No pudimos avisarle: reintentá el envío desde el menú de su fila."
        );
      }
      if (res.outcome === INVITE_OUTCOMES.CREATED && res.cuentaExistente) {
        notas.push("Ese email ya tenía cuenta en Habitia: entra con su email y su contraseña.");
      }
      if (res.fueraDeAlcance) {
        notas.push("No modificamos sus datos: también está en edificios que no administrás.");
      }
      if (res.porteria) {
        notas.push("La unidad es portería: esa persona no va a tener acceso de residente.");
      }
      showToast(mensajes[res.outcome] || "Procesado", "success");
      if (notas.length) showToast(notas.join(" "), "warning");
      onSuccess();

      // Solo un superadmin recibe el link (y solo si falló el aviso): para
      // mandárselo a mano por WhatsApp con el celular del formulario.
      if (res.activationLink) {
        alertDialog("No pudimos avisarle por WhatsApp ni email. Mandale este link vos (vence en 48 horas):", {
          title: "Link de activación",
          content: (
            <LinkActivacion link={res.activationLink} nombre={residente.nombre} celular={residente.celular} />
          ),
        });
      }
    } finally {
      enviandoRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <AdminModal
      open={true}
      onClose={cerrar}
      title="Invitar nuevo residente"
      subtitle={building?.name}
      size="medium"
      icon={<FaUserPlus />}
      footer={
        <>
          <button
            type="button"
            onClick={cerrar}
            disabled={submitting}
            style={btnSecondary}
            className="habitia-btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="invite-form"
            disabled={submitting}
            style={btnPrimary}
            className="habitia-btn-primary"
          >
            {submitting ? "Enviando..." : "Invitar"}
          </button>
        </>
      }
    >
      <form id="invite-form" onSubmit={handleSubmit}>
        {/* El documento va PRIMERO: es la llave de la búsqueda — si la persona
            ya existe, se avisa y se precarga su nombre. Email, celular y
            documento los tipea el staff (no salen de la ficha). */}
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: spacing[3] }}>
          <FormField label="Tipo doc" htmlFor="tipoDocumento" required>
            <select
              id="tipoDocumento"
              name="tipoDocumento"
              value={form.tipoDocumento}
              onChange={handleChange}
              required
              style={inputBase}
              className="habitia-input"
            >
              <option>CI</option>
              <option>DNI</option>
              <option>Pasaporte</option>
              <option>RUT</option>
              <option>Otro</option>
            </select>
          </FormField>
          <FormField label="Documento" htmlFor="documento" required>
            <input
              id="documento"
              name="documento"
              type="text"
              autoFocus
              inputMode={isNumericDocType(form.tipoDocumento) ? "numeric" : "text"}
              placeholder={isNumericDocType(form.tipoDocumento) ? "Solo números — busca si ya existe" : "Letras y números — busca si ya existe"}
              value={form.documento}
              onChange={handleChange}
              required
              maxLength={40}
              style={inputBase}
              className="habitia-input"
            />
          </FormField>
        </div>

        {(() => {
          // Banner de la búsqueda: qué va a pasar al invitar según lo que encontramos.
          if (lookup.status === "idle") return null;
          const base = {
            marginTop: spacing[3],
            padding: `${spacing[2]}px ${spacing[3]}px`,
            borderRadius: radius.sm,
            fontSize: 13,
            lineHeight: 1.5,
          };
          const res = lookup.result || {};
          if (lookup.status === "searching") {
            return (
              <p style={{ fontSize: 12, color: colors.gray600, marginTop: spacing[2] }}>
                Buscando si la persona ya existe…
              </p>
            );
          }
          if (lookup.status === "none") {
            return (
              <p style={{ fontSize: 12, color: colors.gray600, marginTop: spacing[2] }}>
                {sendInvitationEmail
                  ? "No existe en la plataforma — se creará la cuenta y se enviará la invitación."
                  : "No existe en la plataforma — se creará la cuenta sin enviar la invitación."}
              </p>
            );
          }
          if (lookup.status === "conflict") {
            return (
              <div style={{ ...base, background: colors.errorLight, color: colors.errorDark }}>
                El email pertenece a {quienEs(res.conflicto?.porEmail)} y el documento a{" "}
                {quienEs(res.conflicto?.porDocumento)} — son personas distintas. Corregí uno de los
                dos antes de invitar.
              </div>
            );
          }
          if (lookup.status === "otherRole") {
            const rolLabel = ROLE_LABELS[Number(res.otroRol?.typeuser)] || "otro rol";
            return (
              <div style={{ ...base, background: colors.infoLight, color: colors.infoDark }}>
                {quienEs(res.otroRol)} ya existe en la plataforma ({rolLabel}). Al invitar se le crea
                su ficha de residente aparte; si ya tiene cuenta, entra con su email y su contraseña.
              </div>
            );
          }
          // found: residente existente.
          const p = res.precarga || {};
          const completa = res.precargaCompleta === true;
          const emailRef = String(p.emailEnmascarado || enmascararEmail(p.email)).trim();
          const nombre = completa
            ? [p.nombre, p.apellidos].filter(Boolean).join(" ") || emailRef || "La persona"
            : quienEs(p);
          // Referencia para validar lo que se tipea, sin poder copiarlo: el email
          // enmascarado y el final del celular y del documento (de la llave de la
          // búsqueda no hace falta: la tipeó el staff).
          const celFin = finalDe(p.celularEnmascarado || p.celular);
          const docFin = finalDe(p.documentoEnmascarado || p.documento);
          const refs = [
            res.coincidencia !== "email" && emailRef ? `email ${emailRef}` : "",
            celFin ? `celular terminado en ${celFin}` : "",
            res.coincidencia !== "documento" && docFin ? `documento terminado en ${docFin}` : "",
          ].filter(Boolean);
          const precarga = completa
            ? "Precargamos su nombre; email, celular y documento tipealos vos, como te los dio la persona." +
              (refs.length ? ` En su ficha: ${refs.join(", ")}.` : "")
            : "Está en otro edificio: sus datos no se precargan ni se modifican desde acá.";
          const coincidencia = res.requiereConfirmacion
            ? " El documento coincide pero el email es otro: al invitar te vamos a pedir que confirmes que es la misma persona."
            : "";
          if (res.accountState === ACCOUNT_STATES.ACTIVE) {
            return (
              <div style={{ ...base, background: colors.successLight, color: colors.successDark }}>
                {nombre} ya tiene cuenta activa. {precarga} Al invitar solo se lo asigna a esta unidad
                (sin invitación nueva; sus datos personales no se modifican).{coincidencia}
              </div>
            );
          }
          if (res.accountState === ACCOUNT_STATES.ARCHIVED) {
            return (
              <div style={{ ...base, background: colors.warningLight, color: colors.warningDark }}>
                {nombre} existe pero su cuenta está archivada. {precarga} Al invitar se la reactiva y
                se la asigna a esta unidad.{coincidencia}
              </div>
            );
          }
          const stateLabel = ACCOUNT_STATE_LABELS[res.accountState] || "pendiente";
          return (
            <div style={{ ...base, background: colors.warningLight, color: colors.warningDark }}>
              {nombre} ya existe con invitación en estado {String(stateLabel).toLowerCase()}.{" "}
              {precarga}{" "}
              {sendInvitationEmail
                ? "Al invitar se le manda un link nuevo y se lo asigna a esta unidad."
                : "Al invitar se lo asigna a esta unidad, sin mandarle la invitación."}
              {coincidencia}
            </div>
          );
        })()}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3], marginTop: spacing[3] }}>
          <FormField label="Unidad" htmlFor="apartmentId" required>
            <select
              id="apartmentId"
              name="apartmentId"
              value={form.apartmentId}
              onChange={handleChange}
              required
              style={inputBase}
              className="habitia-input"
            >
              <option value="">Elegí una unidad</option>
              {apartments
                .slice()
                .sort((a, b) =>
                  String(a.number || "").localeCompare(String(b.number || ""), undefined, { numeric: true })
                )
                .map((apt) => (
                  <option key={apt.id} value={apt.id}>
                    {apt.number} {apt.floor ? `(piso ${apt.floor})` : ""}
                  </option>
                ))}
            </select>
          </FormField>

          <FormField label="Rol" htmlFor="rol" required>
            <select
              id="rol"
              name="rol"
              value={form.rol}
              onChange={handleChange}
              style={inputBase}
              className="habitia-input"
            >
              {rolesDisponibles.map((r) => (
                <option key={r.label} value={r.label}>
                  {r.label}
                </option>
              ))}
            </select>
          </FormField>
          {esAlquilerDelEdificio(unidadElegida) && (
            <p style={{ gridColumn: "1 / -1", margin: 0, fontSize: typography.sizes.sm, color: colors.gray600 }}>
              Alquiler del edificio: el dueño es el edificio, invitá solo inquilinos. Va a poder entrar a la plataforma y pagar el alquiler.
            </p>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3], marginTop: spacing[3] }}>
          <FormField label="Nombre" htmlFor="nombre" required>
            <input
              id="nombre"
              name="nombre"
              type="text"
              value={form.nombre}
              onChange={handleChange}
              required
              maxLength={80}
              style={inputBase}
              className="habitia-input"
            />
          </FormField>

          <FormField label="Apellidos" htmlFor="apellidos" required>
            <input
              id="apellidos"
              name="apellidos"
              type="text"
              value={form.apellidos}
              onChange={handleChange}
              required
              maxLength={80}
              style={inputBase}
              className="habitia-input"
            />
          </FormField>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3], marginTop: spacing[3] }}>
          <FormField label="Email" htmlFor="email" required>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              style={inputBase}
              className="habitia-input"
            />
          </FormField>

          <FormField label="Celular" htmlFor="celular" required>
            <input
              id="celular"
              name="celular"
              type="tel"
              value={form.celular}
              onChange={handleChange}
              required
              style={inputBase}
              className="habitia-input"
              placeholder="099123456"
            />
          </FormField>
        </div>

        <div style={{ marginTop: spacing[3] }}>
          <FormField label="Fecha de ingreso" htmlFor="fechaIngreso">
            <input
              id="fechaIngreso"
              name="fechaIngreso"
              type="date"
              value={form.fechaIngreso}
              onChange={handleChange}
              style={inputBase}
              className="habitia-input"
            />
          </FormField>
          <p style={{ fontSize: 12, color: colors.gray600, marginTop: spacing[2], lineHeight: 1.5 }}>
            Si la unidad ya tiene a alguien en ese rol y lo reemplazás, esta fecha es el corte:
            el ocupante saliente paga los gastos comunes hasta el día anterior y el nuevo desde
            esta fecha. El saliente conserva acceso de solo-lectura a su histórico.
          </p>
        </div>

        {error && (
          <p role="alert" style={{ color: colors.error, fontSize: 13, marginTop: spacing[3] }}>{error}</p>
        )}

        {revision && (
          <div
            role="status"
            style={{
              marginTop: spacing[3],
              padding: `${spacing[2]}px ${spacing[3]}px`,
              borderRadius: radius.sm,
              background: colors.warningLight,
              color: colors.warningDark,
              fontSize: typography.sizes.base,
              lineHeight: 1.5,
            }}
          >
            {revision} No asignamos la unidad ni enviamos la invitación.
          </div>
        )}

        <label
          htmlFor="send-invitation-email"
          style={{
            display: "flex",
            alignItems: "center",
            gap: spacing[2],
            marginTop: spacing[4],
            fontSize: 13,
            color: colors.gray700,
            cursor: "pointer",
          }}
        >
          <input
            id="send-invitation-email"
            type="checkbox"
            checked={sendInvitationEmail}
            onChange={(e) => setSendInvitationEmail(e.target.checked)}
            style={{ width: 16, height: 16, cursor: "pointer", accentColor: colors.primary }}
          />
          Enviar mail de invitación al residente
        </label>

        <p style={{ fontSize: 12, color: colors.gray600, marginTop: spacing[3], lineHeight: 1.5 }}>
          Empezá por el documento: si la persona ya existe, te avisamos y precargamos su nombre.
          El email, el celular y el documento tipealos siempre vos, como te los dio la persona.
        </p>
      </form>
    </AdminModal>
  );
};

// Link de activación para mandar a mano (solo superadmin hasta las reglas 0D):
// cuando el aviso no salió o el navegador no dejó copiarlo solo. Vive en el
// diálogo: no se guarda ni se loguea.
const LinkActivacion = ({ link, nombre, celular, vence }) => {
  const [copiado, setCopiado] = useState(false);
  const cel = String(celular || "").trim();
  // Los internacionales ya vienen en E.164; los uruguayos se normalizan.
  const tel = toWaMeDigits(cel.startsWith("+") ? cel : normalizePhoneUY(cel));
  const saludo = String(nombre || "").trim();
  const texto =
    `¡Hola${saludo ? ` ${saludo}` : ""}! La administración de tu edificio te invitó a Habitia. ` +
    `Activá tu cuenta en este link (vence en 48 horas):\n\n${link}`;
  const cuando = formatVence(vence);
  const copiar = () => {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(link).then(
      () => setCopiado(true),
      () => setCopiado(false)
    );
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
      <input
        readOnly
        value={link}
        aria-label="Link de activación"
        onFocus={(e) => e.target.select()}
        style={inputBase}
        className="habitia-input"
      />
      <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
        <button type="button" onClick={copiar} style={btnSecondary} className="habitia-btn-secondary">
          <FaCopy /> {copiado ? "Copiado" : "Copiar link"}
        </button>
        {tel && (
          <a
            href={`https://wa.me/${tel}?text=${encodeURIComponent(texto)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...btnSecondary, textDecoration: "none" }}
            className="habitia-btn-secondary"
          >
            <FaWhatsapp /> Abrir WhatsApp
          </a>
        )}
      </div>
      {cuando && (
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Vence el {cuando}.</span>
      )}
    </div>
  );
};

// Validaciones simples para el formulario de edición.
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
const isValidPhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 13;
};
// "Sin nombre" / "Sin email" son sentinelas que pone fetchUsersByUserIds cuando
// el campo falta — no queremos arrastrarlos al formulario de edición.
const cleanSentinel = (val, sentinel) => {
  const v = String(val || "").trim();
  return v === sentinel ? "" : v;
};
const sinEspaciosDeMas = (v) => String(v || "").replace(/\s+/g, " ").trim();

// Modal para corregir los datos de contacto de un residente ya cargado
// (nombre, apellido, email, celular, documento) con updateResidentContact, y
// la fecha de ingreso de su tenencia vigente con occupancyAction. No reasigna
// unidad ni rol: para eso está el flujo de invitar/reasignar con proración.
const EditModal = ({ user, buildingId, esSuperadmin, activeOccupancy = null, onClose, onSuccess, onRefrescar }) => {
  const { confirmDialog } = useAppDialog();
  // Cuenta ya activada (aunque esté archivada): su email es el de inicio de sesión.
  const yaActivada = computeAccountState({ ...user, archivedAt: null }) === ACCOUNT_STATES.ACTIVE;
  // Hasta las reglas 0D ese email lo cambia solo un superadmin (el servidor lo rechaza).
  const emailBloqueado = yaActivada && !esSuperadmin;
  const [submitting, setSubmitting] = useState(false);
  const enviandoRef = useRef(false);
  const [error, setError] = useState("");
  const [contactoIntento] = useIntentoId();
  const [fechaIntento] = useIntentoId();
  // R5/T2: fecha de ingreso de la tenencia activa (dateFrom). El sentinel de
  // "desde siempre" se muestra vacío para no confundir.
  const occFechaIngreso =
    activeOccupancy?.dateFrom && activeOccupancy.dateFrom !== SENTINEL_START
      ? String(activeOccupancy.dateFrom)
      : "";
  const [form, setForm] = useState({
    nombre: cleanSentinel(user.NombreCompleto || user.name, "Sin nombre"),
    apellidos: String(user.ApellidosCompleto || "").trim(),
    email: cleanSentinel(user.email, "Sin email"),
    celular: user.celular != null ? String(user.celular).trim() : "",
    documento: user.Documento != null ? String(user.Documento).trim() : "",
    tipoDocumento: DOC_TYPES.includes(user.tipoDocumento) ? user.tipoDocumento : "CI",
    fechaIngreso: occFechaIngreso,
  });
  // Último estado guardado: solo se manda lo que cambió y, si la fecha falla
  // después de guardar el contacto, el reintento no vuelve a mandar el contacto.
  const baseRef = useRef(form);
  const contactoGuardadoRef = useRef(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const cerrar = () => {
    if (!submitting && !enviandoRef.current) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enviandoRef.current) return;
    setError("");
    const base = baseRef.current;

    // Solo lo que cambió (contrato updateResidentContact).
    const cambios = {};
    const nombre = sinEspaciosDeMas(form.nombre);
    if (!nombre) return setError("El nombre es obligatorio.");
    if (nombre !== sinEspaciosDeMas(base.nombre)) cambios.nombre = nombre;
    const apellidos = sinEspaciosDeMas(form.apellidos);
    if (apellidos !== sinEspaciosDeMas(base.apellidos)) cambios.apellidos = apellidos;
    const email = String(form.email || "").trim();
    if (!emailBloqueado && email.toLowerCase() !== String(base.email || "").trim().toLowerCase()) {
      if (!isValidEmail(email)) return setError("Email inválido.");
      cambios.email = email;
    }
    const celular = String(form.celular || "").trim();
    if (celular !== String(base.celular || "").trim()) {
      if (!celular || !isValidPhone(celular)) return setError("Celular inválido (8 a 13 dígitos).");
      cambios.celular = celular;
    }
    const documento = String(form.documento || "").trim();
    if (documento !== String(base.documento || "").trim() || form.tipoDocumento !== base.tipoDocumento) {
      if (!documento) return setError("El documento no se puede dejar vacío.");
      const docMsg = getDocumentoValidationMessage(form.tipoDocumento, documento);
      if (docMsg) return setError(docMsg);
      // Van juntos: el servidor valida y normaliza el documento con su tipo.
      cambios.documento = documento;
      cambios.tipoDocumento = form.tipoDocumento;
    }
    const hayContacto = Object.keys(cambios).length > 0;
    const cambiaFecha = !!activeOccupancy && form.fechaIngreso !== base.fechaIngreso;
    if (!hayContacto && !cambiaFecha) {
      onSuccess({ contacto: contactoGuardadoRef.current, fecha: null });
      return;
    }

    enviandoRef.current = true;
    try {
      if (cambios.email && yaActivada) {
        const ok = await confirmDialog(
          "Cambia el email con el que inicia sesión y se cierran sus sesiones. Le avisamos al email anterior. ¿Seguir?",
          { title: "Cambiar email de inicio de sesión", confirmLabel: "Cambiar email", danger: true }
        );
        if (!ok) return;
      }
      setSubmitting(true);

      if (hayContacto) {
        const datos = { buildingId, destino: { userDocId: user.id }, ...cambios };
        try {
          // telefono_compartido → confirmar y reintentar con confirmarCompartido.
          const r = await llamarConfirmando(
            updateResidentContact,
            { ...datos, requestId: contactoIntento(JSON.stringify(datos)) },
            confirmDialog
          );
          if (r.cancelado) return;
          contactoGuardadoRef.current = r.res;
          baseRef.current = { ...form, fechaIngreso: base.fechaIngreso };
        } catch (err) {
          console.error("[EditModal] updateResidentContact falló:", codigoDeError(err));
          setError(mensajeDeError(err, "No se pudieron guardar los cambios."));
          return;
        }
      }

      let fecha = null;
      if (cambiaFecha) {
        // R5/T2: corregir la fecha de ingreso de la tenencia activa ("" = desde siempre).
        const datos = {
          op: "corregir_fechas",
          occupancyDocId: activeOccupancy.id,
          dateFrom: form.fechaIngreso || null,
        };
        const parcial = hayContacto ? "Los datos se guardaron, pero la fecha de ingreso no" : "";
        try {
          const r = await llamarConfirmando(
            occupancyAction,
            { ...datos, requestId: fechaIntento(JSON.stringify(datos)) },
            confirmDialog
          );
          if (r.cancelado) {
            if (parcial) {
              onRefrescar?.();
              setError(`${parcial}.`);
            }
            return;
          }
          fecha = r.res;
          baseRef.current = { ...baseRef.current, fechaIngreso: form.fechaIngreso };
        } catch (err) {
          console.error("[EditModal] occupancyAction corregir_fechas falló:", codigoDeError(err));
          const msg = mensajeDeError(err, "No se pudo actualizar la fecha de ingreso.");
          if (parcial) onRefrescar?.();
          setError(parcial ? `${parcial}: ${msg}` : msg);
          return;
        }
      }

      onSuccess({ contacto: contactoGuardadoRef.current, fecha });
    } finally {
      enviandoRef.current = false;
      setSubmitting(false);
    }
  };

  let emailHint;
  if (emailBloqueado) emailHint = MSJ_EMAIL_ACTIVADA;
  else if (yaActivada) emailHint = "Es el email con el que inicia sesión: si lo cambiás, se cierran sus sesiones.";

  return (
    <AdminModal
      open={true}
      onClose={cerrar}
      title="Editar datos del residente"
      subtitle={`${user.NombreCompleto || user.name || ""}`.trim() || undefined}
      size="medium"
      icon={<FaPen />}
      footer={
        <>
          <button
            type="button"
            onClick={cerrar}
            disabled={submitting}
            style={btnSecondary}
            className="habitia-btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="edit-form"
            disabled={submitting}
            style={btnPrimary}
            className="habitia-btn-primary"
          >
            {submitting ? "Guardando..." : "Guardar cambios"}
          </button>
        </>
      }
    >
      <form id="edit-form" onSubmit={handleSubmit}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3] }}>
          <FormField label="Nombre" htmlFor="edit-nombre" required>
            <input
              id="edit-nombre"
              name="nombre"
              type="text"
              value={form.nombre}
              onChange={handleChange}
              required
              maxLength={80}
              style={inputBase}
              className="habitia-input"
            />
          </FormField>
          <FormField label="Apellidos" htmlFor="edit-apellidos">
            <input
              id="edit-apellidos"
              name="apellidos"
              type="text"
              value={form.apellidos}
              onChange={handleChange}
              maxLength={80}
              style={inputBase}
              className="habitia-input"
            />
          </FormField>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3], marginTop: spacing[3] }}>
          <FormField label="Email" htmlFor="edit-email" required hint={emailHint}>
            <input
              id="edit-email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required={!emailBloqueado}
              disabled={emailBloqueado}
              style={
                emailBloqueado
                  ? { ...inputBase, background: colors.gray50, color: colors.gray600, cursor: "not-allowed" }
                  : inputBase
              }
              className="habitia-input"
            />
          </FormField>
          <FormField label="Celular" htmlFor="edit-celular" required>
            <input
              id="edit-celular"
              name="celular"
              type="tel"
              value={form.celular}
              onChange={handleChange}
              required
              style={inputBase}
              className="habitia-input"
              placeholder="099123456"
            />
          </FormField>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: spacing[3], marginTop: spacing[3] }}>
          <FormField label="Tipo doc" htmlFor="edit-tipoDocumento">
            <select
              id="edit-tipoDocumento"
              name="tipoDocumento"
              value={form.tipoDocumento}
              onChange={handleChange}
              style={inputBase}
              className="habitia-input"
            >
              <option>CI</option>
              <option>DNI</option>
              <option>Pasaporte</option>
              <option>RUT</option>
              <option>Otro</option>
            </select>
          </FormField>
          <FormField label="Documento" htmlFor="edit-documento">
            <input
              id="edit-documento"
              name="documento"
              type="text"
              value={form.documento}
              onChange={handleChange}
              maxLength={40}
              style={inputBase}
              className="habitia-input"
            />
          </FormField>
        </div>

        {activeOccupancy && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3], marginTop: spacing[3] }}>
            <FormField label="Fecha de ingreso (tenencia)" htmlFor="edit-fechaIngreso">
              <input
                id="edit-fechaIngreso"
                name="fechaIngreso"
                type="date"
                value={form.fechaIngreso}
                onChange={handleChange}
                style={inputBase}
                className="habitia-input"
              />
            </FormField>
            <div style={{ alignSelf: "end", fontSize: 12, color: colors.gray600, lineHeight: 1.5 }}>
              Corregí acá la fecha si el residente se cargó con una fecha de ingreso equivocada.
            </div>
          </div>
        )}

        {error && (
          <p role="alert" style={{ color: colors.error, fontSize: 13, marginTop: spacing[3] }}>{error}</p>
        )}
      </form>
    </AdminModal>
  );
};

// =============================================================================
// Helpers de llamadas a los callables
// =============================================================================

/**
 * requestId de un intento del staff: el mismo mientras el pedido no cambie
 * (las confirmaciones y un reintento después de un corte no repiten la
 * operación: el servidor devuelve la respuesta guardada con repetido:true; un
 * intento que falló no queda guardado), uno nuevo si cambió algún dato.
 * Devuelve [obtener(clave), terminar()].
 */
function useIntentoId() {
  const ref = useRef({ clave: null, id: null });
  const obtener = useCallback((clave) => {
    if (!ref.current.id || ref.current.clave !== clave) {
      ref.current = { clave, id: nuevoRequestId() };
    }
    return ref.current.id;
  }, []);
  const terminar = useCallback(() => {
    ref.current = { clave: null, id: null };
  }, []);
  return [obtener, terminar];
}

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const etiquetaMeses = (meses) =>
  (Array.isArray(meses) ? meses : [])
    .map((m) => `${MESES[Number(m?.month) - 1] || m?.month || ""} ${m?.year || ""}`.trim())
    .filter(Boolean)
    .join(", ");

/** Mensaje del servidor para mes_cerrado, asegurando que se vean los meses. */
function textoMesCerrado(err) {
  const msg = mensajeDeError(err, "Ese cambio toca un mes que ya tiene cierre.");
  const meses = etiquetaMeses(err?.details?.meses);
  if (!meses || msg.toLowerCase().includes(meses.toLowerCase())) return msg;
  return `${msg}\n\nMeses con cierre: ${meses}.`;
}

/**
 * Confirmación que pide el servidor con un failed-precondition: qué preguntar
 * y con qué flag reintentar. null si el error no se confirma (o ese flag ya se
 * mandó: no se pregunta dos veces lo mismo).
 */
function confirmacionPedida(err, datos) {
  if (codigoDeError(err) !== "failed-precondition") return null;
  const det = (err && err.details) || {};
  const msg = mensajeDeError(err);
  if (det.requiereConfirmacion === "mes_cerrado" && datos.confirmarMesCerrado !== true) {
    return {
      flag: { confirmarMesCerrado: true },
      titulo: "Mes con cierre",
      mensaje: textoMesCerrado(err),
      confirmLabel: "Confirmar igual",
      danger: true,
    };
  }
  if (det.motivo === "coincidencia_documento" && det.userDocId && datos.confirmarCoincidencia !== det.userDocId) {
    return {
      flag: { confirmarCoincidencia: det.userDocId },
      titulo: "¿Es la misma persona?",
      mensaje: msg,
      confirmLabel: "Es la misma persona",
    };
  }
  if (det.motivo === "slot_ocupado" && datos.reemplazarOcupante !== true) {
    return {
      flag: { reemplazarOcupante: true },
      titulo: "Lugar ocupado",
      mensaje: `${msg}\n\nLa fecha de ingreso es el corte: quien está hoy responde hasta el día anterior y conserva acceso de solo lectura a su histórico.`,
      confirmLabel: "Reemplazar",
      danger: true,
    };
  }
  if (det.motivo === "telefono_compartido" && datos.confirmarCompartido !== true) {
    return {
      flag: { confirmarCompartido: true },
      titulo: "¿Celular compartido?",
      mensaje: msg,
      confirmLabel: "Guardar igual",
    };
  }
  return null;
}

/**
 * Llama a un callable y, si el servidor pide una confirmación, la pregunta y
 * reintenta con el flag y el MISMO payload (incluido su requestId). Devuelve
 * { res } o { cancelado: true }; los demás errores se propagan.
 */
async function llamarConfirmando(fn, datos, confirmDialog) {
  let actual = datos;
  // Una vuelta por confirmación posible (persona → lugar → mes) y una de margen.
  for (let vuelta = 0; vuelta < 5; vuelta += 1) {
    try {
      return { res: await fn(actual) };
    } catch (err) {
      const c = confirmacionPedida(err, actual);
      if (!c) throw err;
      const ok = await confirmDialog(c.mensaje, {
        title: c.titulo,
        confirmLabel: c.confirmLabel,
        danger: !!c.danger,
      });
      if (!ok) return { cancelado: true };
      actual = { ...actual, ...c.flag };
    }
  }
  throw new Error("No pudimos completar la operación. Reintentá en unos segundos.");
}

/**
 * Copia un texto que llega async (el link sale del callable). Safari exige
 * escribir dentro del gesto del click: ClipboardItem acepta una promesa y se
 * llama antes del primer await. Si no hay soporte, writeText con el texto.
 * Devuelve false si el navegador no dejó copiar; si la promesa falla, el
 * error se propaga.
 */
async function copiarAlPortapapeles(textoPromesa) {
  const clip = typeof navigator !== "undefined" ? navigator.clipboard : null;
  if (clip && typeof clip.write === "function" && typeof window !== "undefined" && typeof window.ClipboardItem === "function") {
    try {
      const blob = textoPromesa.then((t) => new Blob([t], { type: "text/plain" }));
      blob.catch(() => {}); // el error del pedido se informa abajo
      await clip.write([new window.ClipboardItem({ "text/plain": blob })]);
      return true;
    } catch {
      // Sin permiso o sin soporte de promesas: se prueba con writeText.
    }
  }
  const texto = await textoPromesa;
  if (!clip || typeof clip.writeText !== "function") return false;
  try {
    await clip.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

// =============================================================================
// Fechas
// =============================================================================

const TZ_MONTEVIDEO = "America/Montevideo";

// Hoy (AAAA-MM-DD) en Montevideo: el mismo día que usa el servidor por defecto.
function hoyMontevideo() {
  try {
    const partes = new Intl.DateTimeFormat("en-US", {
      timeZone: TZ_MONTEVIDEO,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const parte = (tipo) => partes.find((p) => p.type === tipo)?.value || "";
    const iso = `${parte("year")}-${parte("month")}-${parte("day")}`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  } catch {
    // Intl sin zonas horarias: cae a la fecha local.
  }
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Vencimiento de un link (ISO del servidor) en hora de Montevideo: "21/10 18:30".
function formatVence(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleString("es-UY", {
      timeZone: TZ_MONTEVIDEO,
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d.toLocaleString("es-UY");
  }
}

// "Nombre (em****@x.com)" de lo que devuelve el servidor sobre otra persona.
function quienEs(p) {
  const nombre = String(p?.nombre || "").trim();
  const email = String(p?.emailEnmascarado || "").trim();
  if (nombre && email) return `${nombre} (${email})`;
  return nombre || email || "otra persona";
}

// "ju****@x.com" (como maskEmail del servidor): el email de una ficha como
// referencia en el modal de invitar, sin que se pueda copiar al formulario.
function enmascararEmail(email) {
  const e = String(email || "").trim();
  const at = e.indexOf("@");
  if (at <= 0) return "";
  const local = e.slice(0, at);
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${"*".repeat(Math.max(1, local.length - visible.length))}${e.slice(at)}`;
}

// Últimos 3 caracteres (letras o números) de un dato de la ficha, en claro o ya
// enmascarado por el servidor ("*****456" → "456"); "" si no hay suficientes.
function finalDe(v) {
  const s = String(v || "").replace(/[^0-9A-Za-z]/g, "");
  return s.length >= 3 ? s.slice(-3) : "";
}

// Fecha absoluta en formato local uruguayo (DD/MM/AAAA).
function formatDate(date) {
  return date.toLocaleDateString("es-UY", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Helper local de fechas
function formatRelative(date) {
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `hace ${days} d`;
  return date.toLocaleDateString("es-UY");
}

export default Cuentas;
